import {
  COST_PRICING,
  type CostEntry,
  type CostProvider,
  type CostSummary,
  type CostBudget,
  type CostAlert,
  type CostOptimizationSuggestion,
  type CostAnalytics
} from '@my-roadtrip/types'
import { db } from '../core/database/firebase.js'
import { createLogger } from '../common/utils/logger.js'
import { createBatchBuilder } from '../common/utils/firestore-batch.js'

const logger = createLogger('CostTracker')

// In-memory buffer for batch writes (flush every 10s)
let costBuffer: CostEntry[] = []
let flushTimeout: NodeJS.Timeout | null = null

class CostTrackerService {
  /**
   * Track a cost entry (buffered, batched writes)
   */
  async track(params: {
    provider: CostProvider
    service: string
    cost: number
    usage: number
    unit: string
    userId?: string
    feature?: string
    metadata?: Record<string, unknown>
  }): Promise<void> {
    const entry: CostEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      ...params,
      timestamp: new Date()
    }

    costBuffer.push(entry)

    // Schedule flush if not already scheduled
    flushTimeout ??= setTimeout(() => this.flush(), 10000)

    // Immediate flush if buffer is large
    if (costBuffer.length >= 100) {
      await this.flush()
    }
  }

  /**
   * Flush cost buffer to Firestore
   */
  private async flush(): Promise<void> {
    if (costBuffer.length === 0) return

    const entries = [...costBuffer]
    costBuffer = []

    if (flushTimeout) {
      clearTimeout(flushTimeout)
      flushTimeout = null
    }

    try {
      const batch = createBatchBuilder(db)

      for (const entry of entries) {
        // Filter out undefined values - Firestore doesn't accept them
        const cleanEntry: Record<string, unknown> = {}
        for (const [key, value] of Object.entries(entry)) {
          if (value !== undefined) {
            cleanEntry[key] = value
          }
        }
        batch.addSet('cost_tracking', entry.id, cleanEntry)
      }

      await batch.commit()
      logger.info(`[CostTracker] Flushed ${entries.length} cost entries`)
    } catch (error) {
      logger.error('[CostTracker] Failed to flush entries', { error, count: entries.length })
      // Re-add to buffer for retry
      costBuffer.unshift(...entries)
    }
  }

  /**
   * Get cost summary for a period
   */
  async getSummary(params: {
    startDate: Date
    endDate: Date
    provider?: CostProvider
    userId?: string
  }): Promise<CostSummary> {
    const { startDate, endDate, provider, userId } = params

    let query = db.collection('cost_tracking')
      .where('timestamp', '>=', startDate)
      .where('timestamp', '<=', endDate)

    if (provider) {
      query = query.where('provider', '==', provider)
    }

    if (userId) {
      query = query.where('userId', '==', userId)
    }

    const snapshot = await query.get()
    const entries = snapshot.docs.map(doc => doc.data() as CostEntry)

    return this.calculateSummary(entries)
  }

  /**
   * Calculate summary from entries
   */
  private calculateSummary(entries: CostEntry[]): CostSummary {
    const total = entries.reduce((sum, e) => sum + e.cost, 0)

    // By provider
    const byProvider: Record<string, { cost: number; usage: number; percentage: number }> = {}
    for (const entry of entries) {
      if (!byProvider[entry.provider]) {
        byProvider[entry.provider] = { cost: 0, usage: 0, percentage: 0 }
      }
      byProvider[entry.provider].cost += entry.cost
      byProvider[entry.provider].usage += entry.usage
    }

    // Calculate percentages
    for (const provider in byProvider) {
      byProvider[provider].percentage = total > 0 ? (byProvider[provider].cost / total) * 100 : 0
    }

    // By feature
    const byFeature: Record<string, { cost: number; usage: number; percentage: number }> = {}
    for (const entry of entries) {
      if (entry.feature) {
        if (!byFeature[entry.feature]) {
          byFeature[entry.feature] = { cost: 0, usage: 0, percentage: 0 }
        }
        byFeature[entry.feature].cost += entry.cost
        byFeature[entry.feature].usage += entry.usage
      }
    }

    for (const feature in byFeature) {
      byFeature[feature].percentage = total > 0 ? (byFeature[feature].cost / total) * 100 : 0
    }

    // Top users
    const userCosts: Record<string, { cost: number; usage: number }> = {}
    for (const entry of entries) {
      if (entry.userId) {
        if (!userCosts[entry.userId]) {
          userCosts[entry.userId] = { cost: 0, usage: 0 }
        }
        userCosts[entry.userId].cost += entry.cost
        userCosts[entry.userId].usage += entry.usage
      }
    }

    const topUsers = Object.entries(userCosts)
      .sort(([, a], [, b]) => b.cost - a.cost)
      .slice(0, 10)
      .map(([userId, data]) => ({
        userId,
        userName: userId, // TODO: Fetch real names
        cost: data.cost,
        usage: data.usage
      }))

    return {
      total,
      byProvider: byProvider as any,
      byFeature,
      topUsers,
      trend: {
        current: total,
        previous: 0, // TODO: Calculate from previous period
        change: 0
      }
    }
  }

  /**
   * Get active budgets
   */
  async getBudgets(): Promise<CostBudget[]> {
    const snapshot = await db.collection('cost_budgets')
      .where('enabled', '==', true)
      .get()

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as CostBudget[]
  }

  /**
   * Check budgets and create alerts
   */
  async checkBudgets(): Promise<CostAlert[]> {
    const budgets = await this.getBudgets()
    const alerts: CostAlert[] = []

    const now = new Date()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)

    for (const budget of budgets) {
      const summary = await this.getSummary({
        startDate: startOfMonth,
        endDate: now,
        provider: budget.provider
      })

      const providerCost = summary.byProvider[budget.provider]?.cost || 0
      const percentage = (providerCost / budget.limit) * 100

      if (percentage >= budget.alertThreshold) {
        let severity: 'low' | 'medium' | 'high' | 'critical' = 'medium'
        if (percentage >= 100) severity = 'critical'
        else if (percentage >= 90) severity = 'high'

        alerts.push({
          id: `alert-${Date.now()}-${budget.id}`,
          type: 'budget',
          severity,
          provider: budget.provider,
          message: `${budget.provider} budget at ${percentage.toFixed(0)}% (${providerCost / 100}$ / ${budget.limit / 100}$)`,
          details: {
            current: providerCost,
            threshold: budget.limit,
            expected: budget.limit
          },
          timestamp: now,
          acknowledged: false
        })
      }
    }

    return alerts
  }

  /**
   * Generate optimization suggestions
   */
  async getSuggestions(summary: CostSummary): Promise<CostOptimizationSuggestion[]> {
    const suggestions: CostOptimizationSuggestion[] = []

    // Check OpenAI costs
    const openaiCost = summary.byProvider.openai?.cost || 0
    if (openaiCost > 10000) { // >$100
      suggestions.push({
        id: 'openai-cache',
        type: 'caching',
        priority: 'high',
        provider: 'openai',
        title: 'Cache OpenAI responses',
        description: 'Implement response caching for similar queries to reduce duplicate API calls',
        estimatedSavings: openaiCost * 0.3, // 30% reduction
        effort: 'medium',
        impact: 8,
        actionable: true,
        action: {
          type: 'enable-cache',
          params: { provider: 'openai', ttl: 3600 }
        }
      })
    }

    // Check Mapbox costs
    const mapboxCost = summary.byProvider.mapbox?.cost || 0
    if (mapboxCost > 5000) { // >$50
      suggestions.push({
        id: 'mapbox-batch',
        type: 'batching',
        priority: 'high',
        provider: 'mapbox',
        title: 'Batch Mapbox API calls',
        description: 'Group multiple geocoding requests into single batch calls',
        estimatedSavings: mapboxCost * 0.25,
        effort: 'low',
        impact: 7,
        actionable: true
      })
    }

    // Check for high-cost users
    if (summary.topUsers.length > 0 && summary.topUsers[0].cost > 5000) {
      suggestions.push({
        id: 'rate-limit-users',
        type: 'rate-limiting',
        priority: 'medium',
        provider: 'openai',
        title: 'Implement per-user rate limits',
        description: `Top user spending ${summary.topUsers[0].cost / 100}$/month. Consider usage caps.`,
        estimatedSavings: 0,
        effort: 'medium',
        impact: 6,
        actionable: false
      })
    }

    return suggestions
  }

  /**
   * Get complete analytics
   */
  async getAnalytics(period: 'day' | 'week' | 'month' = 'month'): Promise<CostAnalytics> {
    const now = new Date()
    let startDate: Date

    switch (period) {
      case 'day':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        break
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1)
        break
    }

    const summary = await this.getSummary({ startDate, endDate: now })
    const budgets = await this.getBudgets()
    const alerts = await this.checkBudgets()
    const suggestions = await this.getSuggestions(summary)

    return {
      period,
      summary,
      budgets,
      alerts,
      suggestions,
      forecast: {
        nextMonth: summary.total * 1.1, // Simple 10% growth forecast
        confidence: 0.7,
        based: 'trend'
      },
      efficiency: {
        costPerUser: 0, // TODO: Calculate
        costPerRequest: 0,
        cacheHitRate: 0,
        wastedCost: 0
      }
    }
  }

  /**
   * Track OpenAI usage
   */
  async trackOpenAI(params: {
    model: string
    inputTokens: number
    outputTokens: number
    userId?: string
    feature?: string
  }): Promise<void> {
    const pricing = this.getOpenAIPricing(params.model)
    const cost = (params.inputTokens / 1000 * pricing.input + 
                  params.outputTokens / 1000 * pricing.output) * 100 // Convert to cents

    await this.track({
      provider: 'openai',
      service: params.model,
      cost: Math.round(cost),
      usage: params.inputTokens + params.outputTokens,
      unit: 'tokens',
      userId: params.userId,
      feature: params.feature,
      metadata: {
        inputTokens: params.inputTokens,
        outputTokens: params.outputTokens
      }
    })
  }

  private getOpenAIPricing(model: string): { input: number; output: number } {
    const normalized = model.toLowerCase()
    if (normalized.includes('gpt-4o-mini')) {
      return COST_PRICING.openai['gpt-4o-mini']
    }
    if (normalized.includes('gpt-4o')) {
      return COST_PRICING.openai['gpt-4o']
    }
    if (normalized.includes('gpt-4-turbo')) {
      return COST_PRICING.openai['gpt-4-turbo']
    }
    if (normalized.includes('gpt-4')) {
      return COST_PRICING.openai['gpt-4']
    }
    if (normalized.includes('gpt-3.5')) {
      return COST_PRICING.openai['gpt-3.5-turbo']
    }
    // Default to 4o pricing if model unknown to avoid under-estimating
    return COST_PRICING.openai['gpt-4o']
  }

  /**
   * Cleanup on shutdown
   */
  async shutdown(): Promise<void> {
    if (flushTimeout) {
      clearTimeout(flushTimeout)
    }
    await this.flush()
  }
}

export const costTracker = new CostTrackerService()
